// @flow
import React, {useRef, useCallback, useEffect, useState, useMemo} from 'react';
import {toPng} from 'html-to-image';
import PropTypes from 'prop-types';
import {useParams, useHistory} from 'react-router-dom';
import {makeStyles} from '@material-ui/core/styles';
import {
  Typography,
  Hidden,
  IconButton,
  Divider,
  Grid,
  CircularProgress,
} from '@material-ui/core';
import TierRestrictionModal from '../../../../elements/TierRestrictionModal/TierRestrictionModal';
import AlertDueDateTier from '../../../tiers/components/AlertDueDateTier/AlertDueDateTier';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import {colors} from '../../../../assets/styles/colors';
import {getImageUrl} from '../../../../utils/image_provider';
import {getFileDataUrl} from '../../../file-storage/utils';
import {getFontEmbedCSS} from '../../../../utils/font';
import {
  logCaptureImages,
  buildImageErrorHandler,
} from '../../../../utils/capture';
import {getBasicDomain, useIsFeatureInTier} from '../../../../utils/component';
import {usePage} from '../../../page/utils';
import {commaFormat} from '../../../../utils/number';
import Switch from '../Switch/Switch';
import {
  formatAddressString,
  getAddressFromShippingDetail,
} from '../../../map/utils';
import {
  Mail,
  WhatsApp,
  GetApp as GetAppIcon,
  ArrowBack,
} from '@material-ui/icons';
import {generateWhatsappLink, generateMailLink} from '../../../../utils/share';

import {
  MAP_PAYMENT_METHOD,
  ORDER_STATE_COLORS,
  ORDER_STATE_STRINGS,
} from '../../../../entities';
import type {
  User,
  Order,
  // OrderItem,
  Store,
  PaymentMethod,
  StoreInformation,
  // InvoiceTax,
} from '../../../../entities';
import {useStoreCurrency} from '../../../currency/utils';
import {formatDate} from '../../../../utils/date';
import InlineLoader from '../../../../elements/LoadingSpinner/InlineLoader';

type Props = {
  fetchOrder: ({orderId: string, forVoucher?: boolean}) => void,
  order: Order,
  isFetching: boolean,
  store?: Store,
  paymentMethod?: PaymentMethod,
  getPaymentMethod: ({id: string}) => void,
  user?: User,
  information?: StoreInformation,
};

const useStyles = accentColor =>
  makeStyles(theme => ({
    root: {
      display: 'flex',
      flexDirection: 'column',
      background: '#F8F9FB',
    },
    logo: {
      width: 'auto',
      maxWidth: '100px',
      height: 'auto',
    },
    headerWrapper: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(4, 4, 2, 4),
      background: '#f8f9fb',
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(3, 4, 2, 4),
      },
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(3, 2, 2, 2),
      },
    },
    backButton: {
      background: 'white',
      padding: '4px',
      margin: '0 16px 0 0',
      boxShadow:
        '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    },
    button: {
      textTransform: 'none',
      whiteSpace: 'nowrap',
      minWidth: 'unset',
      padding: '.75rem',
      color: '#55556D',
    },
    content: {
      display: 'flex',
      alignItems: 'flex-start',
      maxWidth: 1300,
      gap: '.75rem',
      padding: theme.spacing(0, 4, 4, 4),
      [theme.breakpoints.only('sm')]: {
        marginBottom: 64,
      },
      [theme.breakpoints.down('xs')]: {
        flexDirection: 'column',
        alignItems: 'center',
        padding: theme.spacing(0, 2, 2, 2),
        marginBottom: 180,
      },
    },
    orderLeftContent: {
      flex: '1 2 auto',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      minWidth: 220,
      marginTop: theme.spacing(2),
      borderRadius: '12px',
      boxShadow:
        '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
      padding: theme.spacing(5, 3, 3, 3),
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(3, 2, 2, 2),
      },
      background: 'white',
    },
    maxWithWrapper: {
      maxWidth: '300px',
      [theme.breakpoints.down('xs')]: {
        maxWidth: '400px',
      },
    },
    row: {
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '6px',
    },
    orderRightContent: {
      flex: '1 2 60%',
      // flexGrow: 3,
      // flexShrink: 3,
      [theme.breakpoints.down('md')]: {
        flex: '1 2 60%',
      },
      [theme.breakpoints.down('xs')]: {
        marginLeft: 0,
      },
      // height: '91vh',
      // overflow: 'auto',
      marginLeft: theme.spacing(4),
      marginTop: theme.spacing(2),
      borderRadius: '12px',
      boxShadow:
        '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
      padding: theme.spacing(2),
      background: 'white',
    },
    receiptLeftContent: {
      flex: '1 2 auto',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      borderRadius: '12px',
      boxShadow:
        '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
      padding: theme.spacing(5, 3, 3, 3),
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(3, 2, 2, 2),
        width: '100%',
      },
      background: 'white',
    },
    receiptRightContent: {
      minWidth: 280,
      flex: '1 2 60%',
      [theme.breakpoints.down('md')]: {
        flex: '1 2 60%',
      },
      borderRadius: '12px',
      boxShadow:
        '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
      padding: theme.spacing(5, 3, 3, 3),
      [theme.breakpoints.down('xs')]: {
        padding: theme.spacing(3, 2, 2, 2),
        marginLeft: 0,
        width: '100%',
      },
      background: 'white',
    },
    statePill: {
      // width: 96,
      color: 'white',
      backgroundColor: accentColor && accentColor.main,
      borderRadius: '12px',
      padding: '2px 13px',
      textAlign: 'center',
    },
    orderNumber: {
      fontWeight: 600,
      whiteSpace: 'nowrap',
      fontSize: '1.125rem',
      color: colors.grey900,
      margin: theme.spacing(1, 0),
    },
    date: {color: colors.grey600, marginBottom: theme.spacing(2)},
    receipt: {
      marginBottom: theme.spacing(2),
      color: '#00B382',
      padding: 2,
      fontSize: '0.875rem',
    },
    stateChangeButtonWrapper: {
      alignSelf: 'stretch',
      [theme.breakpoints.down('xs')]: {
        position: 'fixed',
        bottom: 60,
        left: 0,
        zIndex: 2,
        width: `calc(100% - ${theme.spacing(4)})`,
        margin: theme.spacing(2),
      },
    },
    stateChangeButton: {
      height: 40,
      fontWeight: 600,
      fontSize: '1rem',
      '&:disabled': {
        color: 'white',
        backgroundColor: colors.grey500,
      },
    },
    copyLinkWrap: {
      width: 'fit-content',
      marginTop: theme.spacing(1),
    },
    copyLink: {
      textTransform: 'none',
      borderRadius: 32,
      whiteSpace: 'nowrap',
      minWidth: 'unset',
      color: colors.purple3,
    },
    stateRevertButton: {
      marginTop: theme.spacing(1),
    },
    dividerGrey: {
      border: `1px solid ${colors.grey300}`,
      width: '100%',
      margin: theme.spacing(1, 0, 2, 0),
    },
    cardContent: {
      borderRadius: '8px',
      background: colors.grey100,
      border: `1px solid ${colors.grey200}`,
      padding: theme.spacing(2),
    },
    marginBottom: {
      marginBottom: theme.spacing(2),
    },
    buttonBack: {
      color: colors.purple,
    },
    innerContentTitle: {
      color: colors.grey900,
      fontSize: '0.875rem',
      fontWeight: 600,
    },
    innerContentText: {
      color: colors.grey800,
      fontSize: '0.875rem',
      margin: '2px 0px',
    },
    bold: {
      fontWeight: 600,
    },
    chipPRO: {
      backgroundColor: '#F5F0FF',
      color: colors.purple3,
      borderRadius: '20px',
      marginLeft: '8px',
      padding: '5px 10px',
      fontWeight: 600,
      fontSize: '14px',
      lineHeight: '22px',
    },
    label: {
      fontStyle: 'normal',
      fontWeight: '700',
      fontSize: '12px',
      lineHeight: '16px',
    },
    labelTotal: {
      fontStyle: 'normal',
      fontWeight: '700',
      fontSize: '14px',
      lineHeight: '18px',
    },
    description: {
      fontStyle: 'normal',
      fontWeight: 400,
      fontSize: '14px',
      lineHeight: '22px',
      color: '#2F3037',
    },
    addressLabel: {
      fontWeight: 400,
      fontSize: '14px',
      lineHeight: '22px',
      color: '#2F3037',
    },
    header: {
      // background: colors.grey200,
      width: '100%',
      marginTop: '16px',
      padding: '6px 8px 6px 0',
    },
    voucherTicker: {
      width: '100%',
      maxWidth: '320px',
      background: 'white',
      margin: 'auto',
      padding: '16px',
    },
    verticalDivider: {
      width: '1px',
      height: '14px',
      margin: 'auto 8px',
      border: `1px solid ${colors.grey300}`,
    },
    chip: {
      background: '#F4F4F6',
      borderRadius: '24px',
      width: 'fit-content',
      marginRight: '6px',
    },
    title: {
      color: colors.grey900,
      fontWeight: 600,
      fontSize: 26,
    },
    productRow: {
      display: 'flex',
      // alignItems: 'center',
      gap: '8px',
    },
    productDetail: {
      color: colors.grey900,
      fontSize: 14,
    },
    productOption: {
      color: colors.grey700,
      fontSize: 12,
    },
    productPrice: {
      fontSize: 14,
      whiteSpace: 'nowrap',
      color: colors.grey900,
    },
    divider: {
      border: `1px solid ${colors.grey300}`,
    },
    summary: {
      display: 'flex',
      justifyContent: 'flex-end',
    },
    summaryLine: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '8px',
    },
    smallLabel: {
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: 14,
      lineHeight: '22px',
      color: colors.grey900,
      whiteSpace: 'nowrap',
    },
    voucherLoader: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '8px',
    },
  }));

export default usePage<Props>(Receipt, 'Order Detail');

const CONTINUE_TO_CHAT = 'CONTINUE_TO_CHAT';
const PAY_ON_DELIVERY = 'PAY_ON_DELIVERY';

const POLLING_INTERVAL = 3000;

function Receipt(props: Props) {
  const {
    fetchOrder,
    order,
    user,
    // isFetching,
    store,
    paymentMethod,
    getPaymentMethod,
    information,
  } = props;

  const customer = order?.customer;
  const currency = useStoreCurrency();

  let paymentMethodName = paymentMethod?.displayName || '';

  if (
    paymentMethod &&
    (paymentMethod.paymentMethod === PAY_ON_DELIVERY ||
      paymentMethod.paymentMethod === CONTINUE_TO_CHAT)
  ) {
    paymentMethodName = MAP_PAYMENT_METHOD[paymentMethod.paymentMethod];
  }

  const hasChangeStatusFeature = useIsFeatureInTier('ORDER_STATUS_CHANGE');

  const dateString =
    order &&
    new Intl.DateTimeFormat('es-PE', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    }).format(order.createdAt);

  // const voucherDateString =
  //   order &&
  //   new Intl.DateTimeFormat('es-PE', {
  //     day: '2-digit',
  //     month: '2-digit',
  //     year: '2-digit',
  //   }).format(order.voucher.createdAt);

  const classes = useStyles(ORDER_STATE_COLORS[order && order.orderState])();

  const params = useParams();
  const history = useHistory();

  const {orderId} = params;
  useEffect(() => {
    if (orderId) {
      fetchOrder({orderId});
    }
  }, [fetchOrder, orderId]);

  const [isFetchingVoucher, setIsFetchingVoucher] = useState(true);
  const [showMessage, setShowMessage] = useState(false);
  const [valueSwitch, setValueSwitch] = useState(0);

  const [count, setCount] = useState(0);
  const countRef = useRef(count);

  countRef.current = count;

  useEffect(() => {
    if (!order || !orderId || valueSwitch === 0) return;
    if (order.voucher) {
      setIsFetchingVoucher(true);
      const id = setInterval(() => {
        fetchOrder({orderId, forVoucher: true});

        setCount(v => v + 1);
      }, POLLING_INTERVAL);

      if (countRef.current > 5) {
        clearInterval(id);
        setIsFetchingVoucher(false);
        setShowMessage(true);
      }

      if (order.voucher.fileId || order.voucher.state === 'BUILT') {
        clearInterval(id);
        setIsFetchingVoucher(false);
      }
      return () => {
        clearInterval(id);
        setIsFetchingVoucher(false);
      };
    }
  }, [fetchOrder, isFetchingVoucher, order, orderId, showMessage, valueSwitch]);

  useEffect(() => {
    if (
      !paymentMethod &&
      order &&
      order.invoice &&
      order.invoice.storePaymentMethodProcessorId
    ) {
      getPaymentMethod({id: order.invoice.storePaymentMethodProcessorId});
    }
  }, [getPaymentMethod, order, paymentMethod]);

  const [tierRestrictionModal, setTierRestrictionModal] = useState(false);

  const orderStatePill = useMemo(
    () => (
      <div className={classes.statePill}>
        {ORDER_STATE_STRINGS[order && order.orderState]}
      </div>
    ),
    [classes.statePill, order]
  );

  const refVoucherTicket = useRef<HTMLDivElement | null>(null);

  const handleToogle = (e, newValue) => {
    setValueSwitch(newValue ? newValue : 0);
  };

  const [isDownloading, setIsDownloading] = useState(false);

  const onButtonClick = useCallback(async () => {
    if ((refVoucherTicket.current === null && !order) || isDownloading) {
      return;
    }
    setIsDownloading(true);
    logCaptureImages(refVoucherTicket.current, 'receipt');
    const fontEmbedCSS = await getFontEmbedCSS();
    toPng(refVoucherTicket.current, {
      style: {
        width: '400px',
      },
      cacheBust: true,
      fontEmbedCSS,
      onImageErrorHandler: buildImageErrorHandler('receipt'),
    })
      .then(dataUrl => {
        const link = document.createElement('a');
        link.download = `voucher_${order.id}.png`;
        link.href = dataUrl;
        link.click();
      })
      .catch(err => {
        console.log(err);
      })
      .finally(() => {
        setIsDownloading(false);
      });
  }, [order, isDownloading]);

  const getShippingCost = () => {
    if (!order) return;

    return order.invoice.detail.last_mile_shipping;
  };

  const getShippingPromotionCost = () => {
    if (!order) return;

    return order.invoice.detail.discount?.shipping_promotion_discount;
  };

  const getCoupon = () => {
    if (!order) return;

    return order.invoice.detail.discount?.coupon_discount;
  };

  const getPromotions = () => {
    if (!order) return;

    return order.invoice.detail.discount?.promotion_discount;
  };

  const shippingCost = getShippingCost();
  const shippingPromotionCost = getShippingPromotionCost();

  const coupon = getCoupon();
  const promotions = getPromotions();

  // const promotionApplied = useMemo(() => {
  //   if (!order) return '';
  //   return order &&
  //     order.invoice &&
  //     order.invoice.promotions &&
  //     order.invoice.promotions.length
  //     ? order.invoice.promotions[0]
  //     : undefined;
  // }, [order]);

  const manualModification = useMemo(() => {
    if (order && order.invoice && order.invoice.detail.total) {
      const totalDiscount = Number(
        order.invoice.detail.discount
          ? order.invoice.detail.discount.total_discount || 0
          : 0
      );

      const totalOrder = Number(order.invoice.detail.total);
      const productsSummary = Number(order.invoice?.detail?.products);
      const shippingPrice = Number(order.invoice.detail.last_mile_shipping);
      // const shippingPromotionPrice = Math.abs(
      //   Number(order.invoice.detail.discount?.shipping_promotion_discount ?? 0)
      // );
      const taxesDiscount = Number(order.invoice.detail.taxes || 0);

      // multiply by 10 and divide by 10 to avoid floating point errors
      const manualSubTotal =
        (productsSummary * 10 +
          totalDiscount * 10 +
          shippingPrice * 10 +
          // shippingPromotionPrice * 10 +
          taxesDiscount * 10) /
        10;

      const value = totalOrder - manualSubTotal;
      const sign = value < 0 ? '-' : '+';

      const absValue = Math.abs(parseFloat(value)).toFixed(0);
      if (Number(absValue) === 0) return undefined;
      else return `${sign}(${commaFormat(Math.abs(value), currency?.symbol)})`;
    } else return undefined;
  }, [currency, order]);

  const deliveryNone = order?.deliveryMethod === 'NONE';

  const shippingAddress = useMemo(() => {
    if (order && !deliveryNone) {
      const {shippingDetail} = order;
      const {
        address1,
        address2,
        admin1 = '',
        admin2 = '',
        admin3 = '',
      } = getAddressFromShippingDetail(shippingDetail);

      const admins = formatAddressString(admin1, admin2, admin3);
      let fullAddress = `${address1}${
        address2 ? ', ' + address2 : ''
      }, ${admins}`;

      if (admin1) return fullAddress;
      else
        return (
          <>
            <div>{address1}.</div>
            <div style={{marginBottom: '4px'}}>{address2}</div>
          </>
        );
    } else return '.';
  }, [deliveryNone, order]);

  const [storeLogo, setStoreLogo] = useState(undefined);

  useEffect(() => {
    if (!information) return;
    let cancelled = false;
    getFileDataUrl(information.logoId).then(url => {
      if (!cancelled) setStoreLogo(url);
    });
    return () => {
      cancelled = true;
    };
  }, [information]);

  if (!order) return null;

  const globalDiscount =
    order.invoice.detail?.discount?.global_discount?.discount;

  return (
    <div className={classes.root}>
      <AlertDueDateTier />
      <div className={classes.headerWrapper}>
        <Typography className={classes.title}>
          <IconButton
            className={classes.backButton}
            onClick={() => history.goBack()}
          >
            <ArrowBack />
          </IconButton>
          Ver comprobante
        </Typography>
        {!hasChangeStatusFeature && (
          <span className={classes.chipPRO}>PRO</span>
        )}
      </div>
      {order && (
        <main className={classes.content}>
          <div className={`${classes.receiptLeftContent}`}>
            {orderStatePill}
            <Typography
              className={classes.orderNumber}
            >{`Nº Orden ${order.id}`}</Typography>
            <Typography
              className={classes.date}
            >{`Fecha: ${dateString}`}</Typography>

            {paymentMethod &&
            (order.orderState === 'PAID' ||
              order.orderState === 'DELIVERED') ? (
              <>
                <Grid container className={classes.cardContent}>
                  <Grid item>
                    <Typography className={classes.innerContentTitle}>
                      Monto recibido
                    </Typography>
                  </Grid>
                  <Divider className={classes.divider} />
                  <Grid item>
                    <Typography className={classes.innerContentText}>
                      {commaFormat(
                        order.invoice.detail.total,
                        currency?.symbol
                      )}
                    </Typography>
                  </Grid>
                </Grid>
                <br />
                <Grid container className={classes.cardContent}>
                  <Grid item>
                    <Typography className={classes.innerContentTitle}>
                      Información de pago
                    </Typography>
                  </Grid>
                  <Divider className={classes.divider} />
                  <Grid item>
                    <Typography className={classes.innerContentText}>
                      {`Método de pago:  ${
                        paymentMethod.displayName === CONTINUE_TO_CHAT
                          ? 'Continuar por whatsapp'
                          : paymentMethod.displayName
                      }`}
                    </Typography>
                  </Grid>
                </Grid>
                <br />
              </>
            ) : null}

            <CustomButton
              className={classes.buttonBack}
              startIcon={<ArrowBack color="primary" />}
              onClick={() => history.goBack()}
            >
              Regresar
            </CustomButton>
            <br />
          </div>
          <div className={classes.receiptRightContent}>
            <Grid container spacing={2} className={classes.cardContent}>
              <Grid item xs={12} lg={12}>
                <Hidden xsDown>
                  {store && store.domains && (
                    <div
                      className={classes.row}
                      style={{
                        justifyContent: valueSwitch === 1 ? 'flex-end' : '',
                      }}
                    >
                      {valueSwitch !== 0 ? (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                          }}
                        >
                          Enviar por:{' '}
                          <IconButton
                            size="small"
                            style={{
                              zoom: '0.8',
                              color: '#FFFF',
                              background: '#25D366',
                              margin: 'auto 8px',
                            }}
                            href={generateWhatsappLink(
                              order.customer.phone,
                              `Hola ${
                                order.customer.firstName
                              }, te escribimos de ${store.name}.
                            ${encodeURIComponent(
                              '\n'
                            )}Puedes descargar tu comprobante del siguiente link: https://${getBasicDomain(
                                store.domains
                              )}${getImageUrl(order.voucher.fileId)}.`
                            )}
                            target="_blank"
                            rel="noopener noreferrer"
                            disabled={showMessage || isFetchingVoucher}
                          >
                            <WhatsApp size="small" />
                          </IconButton>
                          Whatsapp
                          <IconButton
                            size="small"
                            style={{
                              zoom: '0.8',
                              color: '#158CFD',
                              background: '#E8F3FF',
                              margin: 'auto 8px',
                            }}
                            href={generateMailLink(
                              order.customer.email,
                              `Comprobante de pago de la orden ${order.id}`,
                              `Hola ${
                                order.customer.firstName
                              }, te escribimos de ${
                                store.name
                              }.${encodeURIComponent(
                                '\n'
                              )}Puedes descargar tu comprobante del siguiente link: https://${getBasicDomain(
                                store.domains
                              )}${getImageUrl(order.voucher.fileId)}.`
                            )}
                            target="_blank"
                            rel="noopener noreferrer"
                            disabled={showMessage || isFetchingVoucher}
                          >
                            <Mail size="small" />
                          </IconButton>
                          Correo
                        </div>
                      ) : (
                        <div></div>
                      )}
                      <div>
                        {valueSwitch !== 0 ? (
                          <IconButton
                            size="small"
                            style={{
                              zoom: '0.8',
                              margin: 'auto 8px',
                            }}
                            href={getImageUrl(order.voucher.fileId)}
                            target="_blank"
                            className={classes.button}
                            rel="noopener noreferrer"
                            disabled={showMessage || isFetchingVoucher}
                          >
                            <GetAppIcon size="small" />
                          </IconButton>
                        ) : (
                          <IconButton
                            size="small"
                            style={{
                              zoom: '0.8',
                              margin: 'auto 5px',
                            }}
                            className={classes.button}
                            onClick={onButtonClick}
                          >
                            {isDownloading ? (
                              <CircularProgress size={20} />
                            ) : (
                              <GetAppIcon size="small" />
                            )}
                          </IconButton>
                        )}
                        Descargar
                      </div>
                    </div>
                  )}
                </Hidden>

                <Hidden smUp>
                  {store && store.domains && (
                    <div className={classes.row}>
                      <div>
                        {valueSwitch !== 0 ? (
                          <IconButton
                            size="small"
                            style={{
                              zoom: '0.8',
                              margin: 'auto 8px',
                            }}
                            href={getImageUrl(order.voucher.fileId)}
                            target="_blank"
                            className={classes.button}
                            rel="noopener noreferrer"
                            disabled={showMessage || isFetchingVoucher}
                          >
                            <GetAppIcon size="small" />
                          </IconButton>
                        ) : (
                          <IconButton
                            size="small"
                            style={{
                              zoom: '0.8',
                              margin: 'auto 8px',
                            }}
                            className={classes.button}
                            onClick={onButtonClick}
                          >
                            {isDownloading ? (
                              <CircularProgress size={20} />
                            ) : (
                              <GetAppIcon size="small" />
                            )}
                          </IconButton>
                        )}
                        Descargar
                      </div>
                      {valueSwitch !== 0 && (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                          }}
                        >
                          Enviar por:{' '}
                          <IconButton
                            size="small"
                            style={{
                              zoom: '0.8',
                              color: '#FFFF',
                              background: '#25D366',
                              margin: 'auto 8px',
                            }}
                            href={generateWhatsappLink(
                              order.customer.phone,
                              `Hola ${
                                order.customer.firstName
                              }, te escribimos de ${store.name}.
                            ${encodeURIComponent(
                              '\n'
                            )}Puedes descargar tu comprobante del siguiente link: https://${getBasicDomain(
                                store.domains
                              )}${getImageUrl(order.voucher.fileId)}.`
                            )}
                            target="_blank"
                            rel="noopener noreferrer"
                            disabled={showMessage || isFetchingVoucher}
                          >
                            <WhatsApp size="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            style={{
                              zoom: '0.8',
                              color: '#158CFD',
                              background: '#E8F3FF',
                              margin: 'auto 8px',
                            }}
                            href={generateMailLink(
                              order.customer.email,
                              `Comprobante de pago de la orden ${order.id}`,
                              `Hola ${
                                order.customer.firstName
                              }, te escribimos de ${store.name}.
                            ${encodeURIComponent(
                              '\n'
                            )}Puedes descargar tu comprobante del siguiente link: https://${getBasicDomain(
                                store.domains
                              )}${getImageUrl(order.voucher.fileId)}.`
                            )}
                            target="_blank"
                            rel="noopener noreferrer"
                            disabled={showMessage || isFetchingVoucher}
                          >
                            <Mail size="small" />
                          </IconButton>
                        </div>
                      )}
                    </div>
                  )}
                </Hidden>
              </Grid>
              <Divider className={classes.divider} />

              <Grid item xs={12}>
                <Switch
                  value={valueSwitch}
                  onChange={handleToogle}
                  labels={['Formato Ticket', 'Formato A4']}
                />
              </Grid>
              {user && store && (
                <Grid item xs={12} sm={12} md={12} lg={12}>
                  {valueSwitch !== 0 ? (
                    <>
                      {showMessage ? (
                        <Typography variant="body2">
                          No se pudo obtener el comprobante. <br />
                          Por favor intenta cambiar de estado de la orden a{' '}
                          <strong>aceptado</strong> y volver a pasarlo a{' '}
                          <strong>pagado</strong> para regenerar el comprobante.
                        </Typography>
                      ) : isFetchingVoucher ? (
                        <div className={classes.voucherLoader}>
                          <InlineLoader loading size={24} />
                          <span>Obteniendo el comprobante...</span>
                        </div>
                      ) : (
                        <>
                          <Hidden xsDown>
                            <iframe
                              id="pdf-frame"
                              style={{width: '100%', height: '375px'}}
                              title="Voucher pdf"
                              src={getImageUrl(order.voucher.fileId)}
                              frameborder="1"
                              scrolling="auto"
                            />
                          </Hidden>
                          <Hidden smUp>
                            <Typography
                              className={classes.innerContentTitle}
                              style={{margin: 16, background: ''}}
                            >
                              No existe vista previa en formato A4 para celular
                            </Typography>
                          </Hidden>
                        </>
                      )}
                    </>
                  ) : (
                    <div style={{margin: 'auto', width: 'fit-content'}}>
                      <div
                        ref={refVoucherTicket}
                        className={classes.voucherTicker}
                      >
                        <Typography className={classes.title} gutterBottom>
                          Recibo
                        </Typography>
                        {store?.name && (
                          <Typography style={{fontSize: 14}} gutterBottom>
                            {store?.name}
                          </Typography>
                        )}

                        <div className={classes.logoWrapper}>
                          <img
                            alt={store?.name || ''}
                            src={storeLogo}
                            className={classes.logo}
                          />
                        </div>

                        <hr className={classes.divider} />

                        <div className={classes.row}>
                          <Typography className={classes.smallLabel}>
                            Número de orden
                          </Typography>
                          <div className={classes.smallDescription}>
                            {order.id}
                          </div>
                        </div>

                        <div className={classes.row}>
                          <Typography className={classes.smallLabel}>
                            Fecha de creación
                          </Typography>
                          <div className={classes.smallDescription}>
                            {formatDate(order?.createdAt, 'DD/MM/YYYY')}
                          </div>
                        </div>

                        <div className={classes.row}>
                          <Typography className={classes.smallLabel}>
                            Fecha de emisión
                          </Typography>
                          <div className={classes.smallDescription}>
                            {formatDate(
                              order.voucher.createdAt ?? new Date(),
                              'DD/MM/YYYY'
                            )}
                          </div>
                        </div>

                        <hr className={classes.divider} />

                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, 49%)',
                            gap: '4px',
                            margin: '1rem 0',
                          }}
                        >
                          <div style={{width: '100%'}}>
                            <Typography
                              className={classes.smallLabel}
                              gutterBottom
                            >
                              Cliente
                            </Typography>

                            <Typography className={classes.description}>
                              {`${customer.firstName} ${customer.lastName}`}
                            </Typography>
                            <Typography className={classes.description}>
                              {customer.phone}
                            </Typography>

                            {customer.identificationNumber && (
                              <div style={{width: '100%', marginTop: '1rem'}}>
                                <Typography
                                  className={classes.smallLabel}
                                  gutterBottom
                                >
                                  DNI:
                                </Typography>

                                <Typography className={classes.description}>
                                  {customer.identificationNumber}
                                </Typography>
                              </div>
                            )}
                          </div>

                          {!deliveryNone && (
                            <div style={{width: '100%'}}>
                              <Typography
                                className={classes.smallLabel}
                                gutterBottom
                              >
                                Envío
                              </Typography>

                              <Typography className={classes.addressLabel}>
                                {shippingAddress}
                              </Typography>
                            </div>
                          )}
                        </div>

                        {/* Products */}
                        <div>
                          <Typography
                            className={classes.smallLabel}
                            gutterBottom
                          >
                            Resumen de Pedido
                          </Typography>

                          <Typography style={{fontSize: 12}} gutterBottom>
                            Cant.
                          </Typography>

                          {order?.items.map((item, idx) => {
                            return (
                              <>
                                <div
                                  key={item.id}
                                  className={classes.productRow}
                                >
                                  <Typography
                                    className={classes.productDetail}
                                    style={{width: '20px'}}
                                  >
                                    {item.quantity}
                                  </Typography>

                                  <div style={{flex: 1}}>
                                    <Typography
                                      className={classes.productDetail}
                                    >
                                      {item.name}
                                    </Typography>
                                    {item.detail?.productOptions?.map(
                                      option => {
                                        return (
                                          <Typography
                                            className={classes.productOption}
                                          >
                                            {option.name} × {option.quantity}
                                          </Typography>
                                        );
                                      }
                                    )}

                                    {item.productVariant
                                      ? Object.entries(item.productVariant).map(
                                          ([key, value]: [string, any]) => {
                                            return (
                                              <Typography
                                                className={
                                                  classes.productDetail
                                                }
                                                style={{fontSize: 12}}
                                                key={key}
                                              >
                                                <span style={{fontWeight: 600}}>
                                                  {key}:&nbsp;
                                                </span>
                                                <span>{value}</span>
                                              </Typography>
                                            );
                                          }
                                        )
                                      : null}
                                  </div>

                                  <Typography className={classes.productPrice}>
                                    {commaFormat(
                                      parseFloat(item.price) * item.quantity,
                                      currency?.symbol
                                    )}
                                  </Typography>
                                </div>
                                <hr className={classes.divider} />
                              </>
                            );
                          })}
                        </div>
                        {/* End products  */}

                        {/* Totals */}

                        <div className={classes.summary}>
                          <div>
                            <div className={classes.summaryLine}>
                              <Typography className={classes.smallLabel}>
                                Subtotal
                              </Typography>

                              <Typography
                                className={classes.description}
                                noWrap
                              >
                                {commaFormat(
                                  order?.invoice?.detail?.products,
                                  currency?.symbol
                                )}
                              </Typography>
                            </div>
                            {shippingCost && (
                              <div className={classes.summaryLine}>
                                <Typography className={classes.smallLabel}>
                                  Delivery
                                </Typography>

                                <Typography
                                  className={classes.description}
                                  noWrap
                                >
                                  {commaFormat(shippingCost, currency?.symbol)}
                                </Typography>
                              </div>
                            )}

                            {globalDiscount && (
                              <div
                                className={classes.summaryLine}
                                style={{color: '#00B282'}}
                              >
                                <Typography
                                  className={classes.smallLabel}
                                  style={{color: '#00B282'}}
                                >
                                  Descuento Manual
                                </Typography>

                                <Typography
                                  className={classes.description}
                                  style={{color: '#00B282'}}
                                  noWrap
                                >
                                  {`- ${commaFormat(
                                    globalDiscount,
                                    currency?.symbol
                                  )}`}
                                </Typography>
                              </div>
                            )}

                            {shippingPromotionCost && (
                              <div
                                className={classes.summaryLine}
                                style={{color: '#00B282'}}
                              >
                                <Typography
                                  className={classes.smallLabel}
                                  style={{color: '#00B282'}}
                                >
                                  Promoción de envío
                                </Typography>

                                <Typography
                                  className={classes.description}
                                  style={{color: '#00B282'}}
                                >
                                  -{' '}
                                  {commaFormat(
                                    Math.abs(parseFloat(shippingPromotionCost)),
                                    currency?.symbol
                                  )}
                                </Typography>
                              </div>
                            )}

                            {coupon && (
                              <div className={classes.summaryLine}>
                                <Typography
                                  className={classes.smallLabel}
                                  style={{
                                    textTransform: 'uppercase',
                                    color: '#00B282',
                                  }}
                                >
                                  {coupon.code}
                                </Typography>

                                <Typography
                                  className={classes.description}
                                  style={{color: '#00B282'}}
                                >
                                  -{' '}
                                  {commaFormat(
                                    Math.abs(parseFloat(coupon.discount)),
                                    currency?.symbol
                                  )}
                                </Typography>
                              </div>
                            )}

                            {promotions &&
                              promotions.map(el => (
                                <div
                                  className={classes.summaryLine}
                                  style={{color: '#00B282'}}
                                >
                                  <Typography
                                    className={classes.smallLabel}
                                    style={{
                                      textTransform: 'uppercase',
                                      color: '#00B282',
                                      wordBreak: 'break-word',
                                      whiteSpace: 'wrap',
                                    }}
                                  >
                                    {el.name}
                                  </Typography>

                                  <Typography
                                    className={classes.description}
                                    style={{color: '#00B282'}}
                                  >
                                    -{' '}
                                    {commaFormat(
                                      Math.abs(parseFloat(el.discount)),
                                      currency?.symbol
                                    )}
                                  </Typography>
                                </div>
                              ))}
                            {manualModification && (
                              <div className={classes.summaryLine}>
                                <Typography className={classes.smallLabel}>
                                  Ajuste manual
                                </Typography>

                                <Typography className={classes.description}>
                                  {manualModification}
                                </Typography>
                              </div>
                            )}
                            {manualModification && (
                              <div className={classes.summaryLine}>
                                <Typography className={classes.smallLabel}>
                                  Ajuste manual
                                </Typography>

                                <Typography className={classes.description}>
                                  {manualModification}
                                </Typography>
                              </div>
                            )}
                            <div
                              className={classes.summaryLine}
                              style={{marginTop: '1rem'}}
                            >
                              <Typography
                                className={classes.smallLabel}
                                style={{fontSize: 18}}
                              >
                                TOTAL
                              </Typography>

                              <Typography
                                className={classes.description}
                                style={{fontSize: 18}}
                              >
                                {commaFormat(
                                  order?.invoice?.detail?.total,
                                  currency?.symbol
                                )}
                              </Typography>
                            </div>
                          </div>
                        </div>
                        {/* End totals */}

                        <br />
                        <br />
                        <hr className={classes.divider} />
                        <br />

                        <Typography className={classes.smallLabel} gutterBottom>
                          Condiciones y forma de pago
                        </Typography>

                        <Typography className={classes.description}>
                          Método de pago: {paymentMethodName ?? '--'}
                        </Typography>

                        <div style={{height: 20}}></div>
                      </div>
                    </div>
                  )}
                </Grid>
              )}
            </Grid>
          </div>
        </main>
      )}
      <TierRestrictionModal
        open={tierRestrictionModal}
        handleClose={() => setTierRestrictionModal(false)}
      />
    </div>
  );
}

Receipt.propTypes = {
  fetchOrder: PropTypes.func.isRequired,
  order: PropTypes.object,
  isFetching: PropTypes.bool,
  store: PropTypes.object,
  paymentMethod: PropTypes.object,
  getPaymentMethod: PropTypes.func.isRequired,
  user: PropTypes.object,
  information: PropTypes.object,
};
